/**
 * useCurrencies Hook
 * Hook for fetching and caching platform currencies
 */

import { useRequest } from 'ahooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { currencyApi } from '@/services/api/currency.api';
import type { Currency } from '@/types/api/currency';

/**
 * Hook to fetch all available currencies
 * Caches the result for 5 minutes since currencies rarely change
 */
export function useCurrencies() {
  const { i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');

  const { data, error, loading } = useRequest(() => currencyApi.list(), {
    cacheKey: '/api/currencies',
    staleTime: 300000, // 5 minutes
    cacheTime: 300000,
  });

  // Transform currencies to select options with localized names
  const currencyOptions = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((currency: Currency) => ({
      label: `${currency.currencyCode} - ${isZh ? currency.currencyNameZh : currency.currencyNameEn}`,
      value: currency.currencyCode,
      currency, // Include full currency object for additional data access
    }));
  }, [data?.data, isZh]);

  // Create a map for quick lookup by currency code
  const currencyMap = useMemo(() => {
    if (!data?.data) return new Map<string, Currency>();
    return new Map(data.data.map((c: Currency) => [c.currencyCode, c]));
  }, [data?.data]);

  return {
    currencies: data?.data ?? [],
    currencyOptions,
    currencyMap,
    error,
    isLoading: loading,
  };
}

/**
 * Get currency display name by code
 */
export function getCurrencyDisplayName(
  currency: Currency | undefined,
  isZh: boolean
): string {
  if (!currency) return '';
  return isZh ? currency.currencyNameZh : currency.currencyNameEn;
}

/**
 * Get currency symbol by code
 */
export function getCurrencySymbol(
  currencyMap: Map<string, Currency>,
  currencyCode: string
): string {
  const currency = currencyMap.get(currencyCode);
  return currency?.currencySymbol ?? currencyCode;
}

export default useCurrencies;
